import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { BuildPpkgResult, buildPpkg, ppkgErrorCode, ppkgRetryAfterSeconds } from '../api/ppkg';

// FD-321 (FD-ADR-034) — the provisioning-package build, as a state machine.
//
// Split out of the panel for `useDeviceCodeActivation`'s reason: the timing
// policy is deterministic and unit-testable on fake timers, without fighting a
// Fluent harness. It is deliberately much smaller than that hook, because a
// build is ONE call rather than a poll — and keeping it one call is the point.
//
// ── SINGLE-FLIGHT IS STRUCTURAL, AND FD-501 IS WHERE IT BECAME SO ──────────
//
// A build runs ~71 s (every production build is a COLD build — ATC reimages the
// worker after each one). An overlapping second call is a SECOND PACKAGE: a
// second copy of the wave's BPRT, which is a tenant-join credential Rubix
// cannot revoke, and it spends one of ATC's bounded per-migration rebuild
// allowances. Reaching that cap is `409 rebuild_limit_exceeded`, which ATC
// classifies as a consumer-behaviour defect requiring a human. This is the
// FD-185 silent-kill-switch class of defect arriving from the client.
//
// Until FD-501 the guard was a `useRef` inside this hook, and the header here
// argued that clearing it on unmount was unnecessary because "the ref dies with
// the hook instance anyway, so leaving it set costs nothing". That is exactly
// what it cost: the ref dying with the instance IS the guard dying with it.
// Unmount and remount inside the ~71 s window — `/migrations/:id/activate` is a
// SIBLING route, so reaching it unmounts the whole campaign layout and the
// context column with it; so does going back to the campaign list and in again
// — and the hook comes back with a fresh ref and a phase of `idle`. The button
// re-enables while the first request is still on the wire.
//
// Observed live on staging 2026-09-27, migration d52950e1: two `ppkg.build`
// events 36 s apart, the first `delivered` at 13:47:51 and the second refused
// `PACKAGE_ALREADY_DELIVERED` at 13:48:30. That campaign was saved by
// FD-ADR-034 D3's local already-delivered gate. A remount a minute EARLIER —
// before the row was stamped — would have reached ATC instead.
//
// So the run now lives in a MODULE-SCOPED REGISTRY keyed by migrationId. Its
// lifetime is the migration's, not the component's: a remounting hook ADOPTS
// the run in flight rather than starting a second one, and is handed the
// outcome when it settles. A guard whose scope is narrower than the thing it
// guards is not a guard.
//
// ── AND THERE IS STILL NO INTERVAL ANYWHERE ────────────────────────────────
//
// Nothing here schedules a build. The single timer is the rate-limit countdown,
// which is DISPLAY ONLY and never issues a request — a retry after
// `PPKG_RATE_LIMITED` is still a click. `useQuery` + `refetchInterval` would be
// categorically wrong for the same reason it was wrong for activation: the
// request is a MUTATION, and refetch-on-focus would let a background tab
// regaining focus build a package.

/** Countdown repaint for the rate-limit window. DISPLAY ONLY. */
export const PPKG_TICK_MS = 1_000;

export type PpkgBuildPhase =
  | { kind: 'idle' }
  /** One `build-ppkg` in flight. `startedAt` drives the elapsed hint. */
  | { kind: 'building'; startedAt: number }
  | { kind: 'done'; result: BuildPpkgResult }
  | { kind: 'failed'; error: unknown };

export interface PpkgBuild {
  phase: PpkgBuildPhase;
  /**
   * Seconds left on a `PPKG_RATE_LIMITED` window, or 0. Counts down on a
   * display timer; the caller uses it to withhold the retry button until the
   * server's ceiling has passed, because retrying sooner is refused again.
   */
  retryAfterRemaining: number;
  /** Starts a build. Called from a click. A second call while in flight is a NO-OP. */
  build: () => void;
  /** Clears a settled result or error back to idle. Cancels nothing. */
  reset: () => void;
}

// ——— the registry ———————————————————————————————————————————————————————

type Outcome = { ok: true; result: BuildPpkgResult } | { ok: false; error: unknown };

interface BuildRun {
  startedAt: number;
  inFlight: boolean;
  /** Set once the request settles. Held only until a mounted hook adopts it. */
  outcome: Outcome | null;
  listeners: Set<(run: BuildRun) => void>;
}

/**
 * Builds in flight (and settled-but-unclaimed), keyed by `migrationId`.
 *
 * Module scope is the decision, not an implementation detail: the thing being
 * guarded is "at most one build per campaign at a time", and a campaign
 * outlives any component that happens to be showing it.
 */
const runs = new Map<string, BuildRun>();

/** Test-only. Production never clears the registry; runs retire themselves. */
export function __resetPpkgBuildRegistry(): void {
  runs.clear();
}

/** True while a build for this campaign is on the wire, whoever started it. */
export function isPpkgBuildInFlight(migrationId: string): boolean {
  return runs.get(migrationId)?.inFlight === true;
}

function settle(migrationId: string, run: BuildRun, outcome: Outcome): void {
  run.inFlight = false;
  run.outcome = outcome;

  const listeners = [...run.listeners];
  listeners.forEach((listener) => listener(run));

  // A live listener has already rendered the outcome, so the entry has done its
  // job. With NO listener — the panel was unmounted when the build finished —
  // the entry is kept so the next mount can adopt the result rather than
  // showing `idle` for a build that just succeeded.
  if (listeners.length > 0) runs.delete(migrationId);
}

export function usePpkgBuild(migrationId: string, onDone?: () => void): PpkgBuild {
  const [phase, setPhase] = useState<PpkgBuildPhase>({ kind: 'idle' });
  const [retryAfterRemaining, setRetryAfterRemaining] = useState(0);
  const queryClient = useQueryClient();

  const tickTimer = useRef<number | undefined>(undefined);

  // Read through a ref: these fire from an async continuation that outlives the
  // render they were started in, so capturing them would invoke a stale closure.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const clearTick = useCallback(() => {
    if (tickTimer.current !== undefined) window.clearInterval(tickTimer.current);
    tickTimer.current = undefined;
  }, []);

  const startRetryWindow = useCallback(
    (seconds: number) => {
      clearTick();
      setRetryAfterRemaining(seconds);
      const deadline = Date.now() + seconds * 1_000;
      tickTimer.current = window.setInterval(() => {
        const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1_000));
        setRetryAfterRemaining(left);
        if (left <= 0) clearTick();
      }, PPKG_TICK_MS);
    },
    [clearTick],
  );

  /**
   * Apply a settled run to this instance. Shared by the live listener and the
   * adopt-on-mount path, so a build that finished while the panel was unmounted
   * reaches exactly the same state as one that finished while it was open —
   * including the migration invalidation, which would otherwise be lost with the
   * component that started it.
   */
  const applyOutcome = useCallback(
    (outcome: Outcome) => {
      if (outcome.ok) {
        setPhase({ kind: 'done', result: outcome.result });
      } else {
        setPhase({ kind: 'failed', error: outcome.error });
        // `PPKG_RATE_LIMITED`'s Retry-After is a CEILING, not a hint: retrying
        // sooner re-enters the same limit. The countdown exists so the button is
        // withheld rather than offered-and-refused.
        if (ppkgErrorCode(outcome.error) === 'PPKG_RATE_LIMITED') {
          const seconds = ppkgRetryAfterSeconds(outcome.error);
          if (seconds !== undefined) startRetryWindow(seconds);
        }
      }
      // A READ, not a build. Invalidate on failure too: several refusals leave
      // the row CHANGED — `building` once a packageId is reserved, or `stored`
      // after a failed state stamp — and a stale panel would under-report what
      // exists.
      void queryClient.invalidateQueries({ queryKey: ['migration', migrationId] });

      // `onDone` fires on SUCCESS ONLY, and the asymmetry with the invalidate
      // above is deliberate rather than an oversight. The invalidate is this
      // hook's own read-refresh; `onDone` is the caller's "the package changed"
      // signal, and a refusal that built nothing has not changed it. Calling it
      // on failure would make a `409` look like an outcome to every caller.
      if (outcome.ok) onDoneRef.current?.();
    },
    [migrationId, queryClient, startRetryWindow],
  );

  // The listener is read through a ref so subscribing never depends on it. A
  // subscription that churned with every render would detach and reattach
  // around the one moment it exists to catch.
  const applyRef = useRef(applyOutcome);
  applyRef.current = applyOutcome;

  const subscription = useRef<{ run: BuildRun; listener: (run: BuildRun) => void } | null>(null);

  const unsubscribe = useCallback(() => {
    const current = subscription.current;
    if (!current) return;
    current.run.listeners.delete(current.listener);
    subscription.current = null;
  }, []);

  /**
   * Listen to a run. EXACTLY ONE subscription per hook instance, dropped on
   * unmount — which is what lets `settle` tell "nobody is watching, keep the
   * result for the next mount" from "it has been shown".
   */
  const subscribe = useCallback(
    (run: BuildRun) => {
      unsubscribe();
      const listener = (settled: BuildRun) => {
        if (settled.outcome) applyRef.current(settled.outcome);
      };
      run.listeners.add(listener);
      subscription.current = { run, listener };
    },
    [unsubscribe],
  );

  // Adopt whatever this campaign's run is doing, on mount and on id change.
  // This is the half that makes the registry worth having: without it the
  // registry would block the second click and leave the panel claiming `idle`.
  useEffect(() => {
    const run = runs.get(migrationId);
    if (!run) return;

    if (run.outcome) {
      // Settled while nothing was mounted. Claim it once, then drop it — a
      // result that outlived its claim would resurrect a success banner on
      // every later visit to the campaign.
      const outcome = run.outcome;
      runs.delete(migrationId);
      applyRef.current(outcome);
      return;
    }

    setPhase({ kind: 'building', startedAt: run.startedAt });
    subscribe(run);
  }, [migrationId, subscribe]);

  useEffect(
    () => () => {
      clearTick();
      unsubscribe();
    },
    [clearTick, unsubscribe],
  );

  const build = useCallback(() => {
    // THE gate. Checked against the registry, so it holds across a remount.
    if (runs.get(migrationId)?.inFlight) return;

    const run: BuildRun = {
      startedAt: Date.now(),
      inFlight: true,
      outcome: null,
      listeners: new Set(),
    };
    // Registered BEFORE the await, synchronously, so two clicks inside one tick
    // cannot both pass the check above. Disabling the button is the visible half
    // of the control; this is the actual control.
    runs.set(migrationId, run);
    subscribe(run);

    clearTick();
    setRetryAfterRemaining(0);
    setPhase({ kind: 'building', startedAt: run.startedAt });

    void (async () => {
      try {
        settle(migrationId, run, { ok: true, result: await buildPpkg(migrationId) });
      } catch (error) {
        settle(migrationId, run, { ok: false, error });
      }
    })();
  }, [clearTick, migrationId, subscribe]);

  const reset = useCallback(() => {
    clearTick();
    setRetryAfterRemaining(0);
    setPhase({ kind: 'idle' });
    // Only an unclaimed SETTLED run is dropped. A run still in flight is left
    // exactly where it is: dismissing a message must never re-open the gate on a
    // request that is still building a credential.
    const run = runs.get(migrationId);
    if (run && !run.inFlight) {
      unsubscribe();
      runs.delete(migrationId);
    }
  }, [clearTick, migrationId, unsubscribe]);

  return { phase, retryAfterRemaining, build, reset };
}
